// Updated TutorProfilePage.js
import React, { useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Container,
  Box,
  Typography,
  Card,
  Avatar,
  Stack,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Button,
  Grid,
  Alert,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { fetchTutorProfile } from '../services/api';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import VerifiedIcon from '@mui/icons-material/Verified';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer'; // make sure it's at the top
import FilledButton from '../components/FilledButton';


const localizer = momentLocalizer(moment);

const VerifiedBadge = () => (
  <CheckCircleIcon sx={{ fontSize: 18, color: 'green', ml: 1 }} />
);

const TutorProfilePage = () => {
  const { id: tutorId } = useParams();
  const [profile, setProfile] = useState(null);
  const [selectedRateId, setSelectedRateId] = useState(null);
  const navigate = useNavigate();



  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data = await fetchTutorProfile(tutorId);
        setProfile(data);
      } catch (error) {
        console.error('Failed to load tutor profile:', error);
      }
    };
    loadProfile();
  }, [tutorId]);

  if (!profile || !profile.user) {
    return <Typography>Loading...</Typography>;
  }

  const {
    user,
    full_name,
    intro,
    subjects = [],
    course_offerings = [],
    availability_slots = [],
    educations = [],
    certificates = [],
    experiences = [],
    location,
    region,
    country,
    time_zone,
  } = profile;

  const VerifiedStatusIcon = ({ date }) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
      <VerifiedIcon sx={{ fontSize: 18, color: 'green', mr: 0.5 }} />
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Verified {moment(date).format('MMM YYYY')}
      </Typography>
    </Box>
  );
  
  const UnverifiedIcon = () => (
    <Typography
      variant="caption"
      sx={{ display: 'inline-flex', alignItems: 'center', ml: 1, color: 'gray' }}
    >
      <HelpOutlineIcon sx={{ fontSize: 18, mr: 0.5 }} />
      Unverified
    </Typography>
  );  

  
  const availabilityEvents = availability_slots.map(slot => {
    const start = new Date(slot.start_time);
    const end = new Date(slot.end_time);
    return {
      title: 'Available',
      start,
      end,
      allDay: false,
    };
  });

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        The Tutoring page is under active development, please stay tuned, and
        feel free to explore, and give us suggestions through the{' '}
        <Link component={RouterLink} to="/feedback">
          feedback
        </Link>{' '}
        page
      </Alert>

    <Box sx={{ mb: 2 }}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon color="secondary" />}
        onClick={() => navigate(-1)}
      >
        Back
      </Button>
    </Box>

    <Card sx={{ mb: 4, p: 3 }}>
      <Grid container spacing={3} alignItems="center">
        <Grid item>
          <Avatar src={user.avatar} sx={{ width: 100, height: 100, mt: -6 }} />
        </Grid>

        <Grid item xs>
          <Typography variant="h4" fontWeight="bold">
            {full_name}
          </Typography>

          {/* Intro */}
          <Typography variant="body2" sx={{ mt: 1 }}>
            {intro}
          </Typography>

          {/* Tutor Details List */}
          <Box
            component="ul"
            sx={{
              listStyle: 'none',
              pl: 0,
              mt: 2,
              mb: 0,
              '& li': {
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                lineHeight: 1.8, // increased line spacing
                mt: 1,
              },
              '& strong': {
                minWidth: '110px', // ensures label width for indenting
                flexShrink: 0,
              },
            }}
          >
            <li>
              <Typography variant="body2">
                <strong>Teaches:</strong>
              </Typography>
              <Typography variant="body2">
                {subjects.map(s => s.name).join(', ') || 'N/A'}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Speaks:</strong>
              </Typography>
              <Typography variant="body2">
                {profile.languages?.length > 0
                  ? profile.languages.map(lang => lang.name).join(', ')
                  : 'N/A'}
              </Typography>
            </li>
            {profile.institutions?.length > 0 && (
              <li>
                <Typography variant="body2">
                  <strong>Affiliated Institutions:</strong>
                </Typography>
                <Typography variant="body2">
                  {profile.institutions.map(i => i.name).join(', ')}
                </Typography>
              </li>
            )}
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>Location:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {[location?.name, region?.name, country?.name].filter(Boolean).join(', ') || 'Unknown'}
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>Timezone:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {time_zone || 'N/A'}
              </Typography>
            </li>
          </Box>
        </Grid>
      </Grid>
    </Card>


    {/* Credentials */}
    <Card sx={{ mt: 4, mb: 4, p: 3 }}>
    <Typography variant="h5" color="secondary" gutterBottom>Credentials & Experience</Typography>

    {/* Education */}
    <Box sx={{ mb: 2 }}>
        <Typography variant="h6">Education</Typography>
        {educations.length > 0 ? (
        <Stack spacing={1}>
            {educations.map((edu) => (
                <Box key={edu.id} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2">
                  {edu.title} {edu.institution && `- ${edu.institution}`}
                </Typography>
                {edu.is_verified
                  ? <VerifiedStatusIcon date={edu.verified_at} />
                  : <UnverifiedIcon />
                }
              </Box>              
            ))}
        </Stack>
        ) : (
        <Typography variant="body2" color="text.secondary">No education listed.</Typography>
        )}
    </Box>

    {/* Certificates */}
    <Box sx={{ mb: 2 }}>
        <Typography variant="h6">Certificates</Typography>
        {certificates.length > 0 ? (
        <Stack spacing={1}>
            {certificates.map((cert) => (
                <Box key={cert.id} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2">
                  {cert.title} {cert.institution && `- ${cert.institution}`}
                </Typography>
                {cert.is_verified
                  ? <VerifiedStatusIcon date={cert.verified_at} />
                  : <UnverifiedIcon />
                }
              </Box>              
            ))}
        </Stack>
        ) : (
        <Typography variant="body2" color="text.secondary">No certificates listed.</Typography>
        )}
    </Box>

    {/* Experience */}
    <Box>
        <Typography variant="h6">Experience</Typography>
        {experiences.length > 0 ? (
        <Stack spacing={1}>
            {experiences.map((exp) => (
            <Box key={exp.id}>
                <Box key={exp.id} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2">
                    {exp.title} {exp.institution && `- ${exp.institution}`}
                  </Typography>
                  {exp.is_verified
                    ? <VerifiedStatusIcon date={exp.verified_at} />
                    : <UnverifiedIcon />
                  }
                </Box>  
                <Typography variant="body2" color="text.secondary">
                {exp.description}
                </Typography>
            </Box>
            ))}
        </Stack>
        ) : (
        <Typography variant="body2" color="text.secondary">No experience listed.</Typography>
        )}
    </Box>
    </Card>


      {/* About Me + Teaching Style */}
      <Card sx={{ mb: 4, p: 3 }}>
        <Typography variant="h5" color="secondary" gutterBottom>About Me</Typography>
        <Typography variant="h6" gutterBottom>Bio</Typography>
        {profile.about_me ? (
            <div dangerouslySetInnerHTML={{ __html: profile.about_me }} />
        ) : (
            <Typography variant="body1" color="text.secondary">No bio provided.</Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom>My Teaching Style</Typography>
        {profile.teaching_style ? (
            <div dangerouslySetInnerHTML={{ __html: profile.teaching_style }} />
        ) : (
            <Typography variant="body1" color="text.secondary">No teaching style provided.</Typography>
        )}
        </Card>


      {/* Stats */}
      <Card sx={{ mb: 4, p: 3 }}>
        <Stack direction="row" spacing={4} justifyContent="space-around" alignItems="center">
            <Box>
            <Typography variant="h6" align="center">Rating</Typography>
            <Typography variant="h5" align="center">
                {profile.average_rating?.toFixed(1) ?? 'N/A'}
            </Typography>
            </Box>
            <Box>
            <Typography variant="h6" align="center">Students</Typography>
            <Typography variant="h5" align="center">
                {profile.total_students ?? 'N/A'}
            </Typography>
            </Box>
            <Box>
            <Typography variant="h6" align="center">Lessons</Typography>
            <Typography variant="h5" align="center">
                {profile.total_lessons ?? 'N/A'}
            </Typography>
            </Box>
            <Box>
            <Typography variant="h6" align="center">Attendance</Typography>
            <Typography variant="h5" align="center">
            {(profile.total_lessons ?? 0) > 0
              ? `${profile.attendance_rate?.toFixed(0) ?? 'N/A'}%`
              : 'N/A'}
            </Typography>
            </Box>
        </Stack>
        </Card>


      {/* Lessons */}
      <Card sx={{ mb: 4, p: 3 }}>
        <Typography variant="h5" color="secondary" gutterBottom>Lesson Offerings</Typography>
        {course_offerings.map(offering => (
          <Accordion key={offering.id}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">{offering.title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
            <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mt: 0.5, mb: 1 }}>
            {offering.description}
            </Typography>
              <Typography variant="caption" display="block">Subject: {offering.subject?.name ?? 'N/A'}</Typography>
              <Typography variant="caption" display="block">Institution: {offering.institution?.name ?? 'N/A'}</Typography>
              <Box mt={2}>
        <Typography variant="h6" gutterBottom>Pricing Options</Typography>
        <Stack spacing={2}>
  {Object.entries(
    offering.rates.reduce((groups, rate) => {
      const key = `${rate.max_students}-${rate.duration_minutes}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(rate);
      return groups;
    }, {})
  ).map(([groupKey, groupRates], index) => {
    const [max_students, duration_minutes] = groupKey.split('-');

    return (
      <Box key={groupKey}>
        {index > 0 && <Divider sx={{ my: 2 }} />}

        <Typography variant="subtitle2" fontWeight="medium" sx={{ mb: 1, color: 'text.secondary' }}>
          1-on-{max_students} • {duration_minutes} minutes
        </Typography>

        <Stack spacing={1}>
          {groupRates
            .sort((a, b) => a.session_count - b.session_count)
            .map(rate => {
              const perSession = parseFloat(rate.price || 0);
              const isChildRate = rate.parent_rate != null;
              const baseRate = offering.rates.find(
                r => r.session_count === 1 && r.max_students === rate.max_students
              );

              let discountChip = null;
              if (baseRate && rate.session_count > 1) {
                const basePrice = parseFloat(baseRate.price || 0);
                const discount = 100 * (1 - perSession / basePrice);
                if (discount > 0) {
                  discountChip = (
                    <Typography variant="body2" color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocalOfferIcon fontSize="small" sx={{ mr: 0.5 }} />
                      {discount.toFixed(0)}% off
                    </Typography>
                  );
                }
              }

              return (
                <Box
                  key={rate.id}
                  onClick={() => setSelectedRateId(prev => (prev === rate.id ? null : rate.id))}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '2px solid',
                    borderColor: selectedRateId === rate.id ? 'primary.main' : '#ddd',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    bgcolor: selectedRateId === rate.id ? '#e3f2fd' : '#f9f9f9',
                    cursor: 'pointer',
                    transition: '0.2s',
                    '&:hover': {
                      ml: isChildRate ? 4 : 0,
                      borderLeft: isChildRate ? '4px solid #ccc' : '2px solid',
                      borderColor: selectedRateId === rate.id ? 'primary.main' : '#ddd',
                      bgcolor: '#e3f2fd',
                    },
                  }}
                >
                  <Typography variant="body2">
                    {rate.session_count} session{rate.session_count > 1 ? 's' : ''}
                  </Typography>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    {discountChip}
                    <Typography variant="body2" fontWeight="bold">
                      ${ (perSession / rate.max_students).toFixed(2) } / session / person
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
        </Stack>
      </Box>
    );
  })}
</Stack>

        </Box>

            </AccordionDetails>
          </Accordion>
          
        ))}

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <FilledButton
            text="Book a Lesson"
            color="secondary"
            size="large"
            disabled={!selectedRateId}
            onClick={() => {
              if (selectedRateId) {
                navigate(`/tutors/book/${selectedRateId}`, {
                  state: { from: `/tutors/${tutorId}` },
                });
              }
            }}
            sx={{ width: '100%', mb: 1 }}
          />
        </Box>



      </Card>

      {/* Availability */}
      <Card sx={{ p: 3 }}>
        <Typography variant="h5" color="secondary" gutterBottom>Availability</Typography>
        {availabilityEvents.length > 0 ? (
          <Calendar
            localizer={localizer}
            events={availabilityEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 500 }}
            defaultView="week"
            views={['week', 'day', 'month']}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No availability slots listed.
          </Typography>
        )}
      </Card>
    </Container>
  );
};

export default TutorProfilePage;
